import { useState, useEffect, useRef, useCallback } from 'react'
import { apiGetNotifications, apiGetUnreadCount, apiMarkNotificationRead, apiMarkAllNotificationsRead, apiDeleteNotification } from '../api'
import type { NotificationItem } from './types'
import { useNavigate } from 'react-router-dom'

interface NotificationCenterProps {
  // No required props — manages its own state
}

export default function NotificationCenter(_props: NotificationCenterProps) {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showPanel, setShowPanel] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLButtonElement>(null)

  const timeAgo = useCallback((dateStr: string) => {
    const now = Date.now()
    const then = new Date(dateStr).getTime()
    const diff = Math.max(0, now - then)
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days < 7) return `${days}d ago`
    return `${Math.floor(days / 7)}w ago`
  }, [])

  const notifIcon = (type: string) => {
    switch (type) {
      case 'deadline_warning': return '\u23F0'
      case 'usage_alert': return '\uD83D\uDCCA'
      case 'appeal_update': return '\u2705'
      case 'system': return '\u2699\uFE0F'
      case 'billing': return '\uD83D\uDCB3'
      default: return '\uD83D\uDD14'
    }
  }

  const fetchUnreadCount = useCallback(() => {
    apiGetUnreadCount().then(r => setUnreadCount(r.unread_count)).catch(e => console.warn('Notification API failed:', e))
  }, [])

  const fetchNotificationList = useCallback(() => {
    apiGetNotifications().then(r => setNotifications(r.notifications)).catch(e => console.warn('Notification API failed:', e))
  }, [])

  // Poll unread count every 60 seconds
  useEffect(() => {
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 60000)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  // Fetch full list when panel opens
  useEffect(() => {
    if (showPanel) fetchNotificationList()
  }, [showPanel, fetchNotificationList])

  // Close panel on outside click
  useEffect(() => {
    if (!showPanel) return
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setShowPanel(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showPanel])

  const handleMarkAllRead = async () => {
    await apiMarkAllNotificationsRead().catch(e => console.warn('Notification API failed:', e))
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    setUnreadCount(0)
  }

  const handleNotifClick = async (n: NotificationItem) => {
    if (!n.read) {
      await apiMarkNotificationRead(n.id).catch(e => console.warn('Notification API failed:', e))
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))
      setUnreadCount(prev => Math.max(0, prev - 1))
    }
    if (n.action_url) {
      setShowPanel(false)
      navigate(n.action_url)
    }
  }

  const handleDeleteNotif = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    await apiDeleteNotification(id).catch(e => console.warn('Notification API failed:', e))
    setNotifications(prev => prev.filter(n => n.id !== id))
    fetchUnreadCount()
  }

  return (
    <div className="relative">
      <button
        ref={bellRef}
        onClick={() => setShowPanel(prev => !prev)}
        className="relative flex items-center gap-1.5 text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-500 rounded-full px-3 py-1.5 hover:bg-slate-100 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown Panel */}
      {showPanel && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-200 z-50 dark:bg-slate-800 dark:border-slate-700"
        >
          {/* Panel Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between rounded-t-xl">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-slate-400">
              No notifications yet. We'll alert you about deadlines, appeal updates, and more.
            </div>
          ) : (
            notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleNotifClick(n)}
                className={`w-full text-left px-4 py-3 flex gap-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
              >
                {/* Unread dot */}
                <div className="flex-shrink-0 pt-1">
                  {!n.read ? (
                    <span className="block w-2 h-2 rounded-full bg-blue-500"></span>
                  ) : (
                    <span className="block w-2 h-2 rounded-full bg-transparent"></span>
                  )}
                </div>
                {/* Icon */}
                <span className="flex-shrink-0 text-base mt-0.5">{notifIcon(n.type)}</span>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-800 truncate">{n.title}</span>
                    <button
                      onClick={(e) => handleDeleteNotif(e, n.id)}
                      className="flex-shrink-0 text-slate-300 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                  <span className="text-[10px] text-slate-400 mt-1 block">{timeAgo(n.created_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
